// deps
import React, { memo, ReactNode, ReactElement, useCallback, useContext, useEffect, useState } from "react";

// styles
import { Container, Content, Header } from "./styles";

// components
import { CartGroupOrderHeader } from "@swiggy-private/cart-ui";
import { Shimmer } from "@cart-client/components/shimmer";
import CartUIWrapper from "@cart-client/components/cart-renderer/cart-ui-wrapper/CartUIWrapper";

// internal-deps
import {
    CheckoutRoute,
    closeWebview,
    getBridgeSdk,
    GlobalHeaderState,
    NEWRELIC_TABLES,
    recordNREvent,
    SwgyHeaderKeysList,
    GroupOrderCartContext,
    useCustomHardwareBack,
} from "@swiggy-private/checkout-core";
import { GetGroupCartOrderResponse } from "@swiggy-private/cart-types";
import { isEmpty } from "@swiggy-private/js-utils";
import { GenericError } from "@swiggy-private/core-ui";

// helpers
import {
    getHostFirstName,
    getTotalPeopleInGroup,
    isCurrentGroupOrderDeleted,
    isCurrentGroupOrderPlaced,
} from "@cart-client/helpers/GroupCartOrder";
import { recordSharedCartPerceivedTime } from "@cart-client/helpers/Metric";

// constants
import { RETRY } from "@cart-client/constants/TextDict";
import { NR_PAGE_ERROR_TYPES } from "@cart-client/constants/GroupOrder";

interface GroupCartUIWrapperProps {
    children: ReactNode;
    isHost: boolean;
    groupCartId: string;
    doManualRefresh: VoidFunction;
    clickGrpCartItemEvent: (label: string) => void;
}

interface GroupCartHeaderWrapperProps {
    isHost: boolean;
    groupCartId: string;
    doManualRefresh: VoidFunction;
    clickGrpCartItemEvent: (label: string) => void;
    groupCartData: GetGroupCartOrderResponse["data"];
}

const HEADER_PILL_ANALYTICS_LABEL = {
    INVITE_TO_GROUP_CART: "invite-grp-cart",
    DELETE_GROUP_CART: "delete-grp-cart",
    LEAVE_GROUP_CART: "leave-grp-cart",
    REFRESH_GROUP_CART: "refresh-grp-cart",
};

function GroupCartHeaderWrapper(props: GroupCartHeaderWrapperProps): ReactElement | null {
    const { isHost, groupCartId, doManualRefresh, clickGrpCartItemEvent, groupCartData } = props;
    const currentUserId = GlobalHeaderState.getHeader(SwgyHeaderKeysList.userId);

    const handleBack = useCallback(() => {
        closeWebview();
    }, []);

    const handleInviteMore = useCallback(() => {
        clickGrpCartItemEvent(HEADER_PILL_ANALYTICS_LABEL.INVITE_TO_GROUP_CART);
        getBridgeSdk()
            .inviteMoreGroupCart({ groupCartId })
            .catch(() => void 0);
    }, [clickGrpCartItemEvent, groupCartId]);

    const handleDeleteGroupCart = useCallback(() => {
        clickGrpCartItemEvent(HEADER_PILL_ANALYTICS_LABEL.DELETE_GROUP_CART);
        getBridgeSdk()
            .deleteGroupCart({ groupCartId })
            .catch(() => void 0);
    }, [clickGrpCartItemEvent, groupCartId]);

    const handleLeaveGroupCart = useCallback(() => {
        clickGrpCartItemEvent(HEADER_PILL_ANALYTICS_LABEL.LEAVE_GROUP_CART);
        getBridgeSdk()
            .leaveGroupCart({ userId: currentUserId, groupCartId })
            .catch(() => void 0);
    }, [clickGrpCartItemEvent, currentUserId, groupCartId]);

    const handleRefreshGroupCart = useCallback(() => {
        clickGrpCartItemEvent(HEADER_PILL_ANALYTICS_LABEL.REFRESH_GROUP_CART);
        doManualRefresh();
    }, [clickGrpCartItemEvent, doManualRefresh]);

    return (
        <Header>
            <CartGroupOrderHeader
                isHost={isHost}
                hostFirstName={getHostFirstName(groupCartData) || ""}
                totalPeopleInGroup={getTotalPeopleInGroup(groupCartData)}
                restaurantName={groupCartData?.restaurant_details?.restaurant_name}
                restaurantArea={groupCartData?.restaurant_details?.area_name}
                handleBack={handleBack}
                handleInviteMore={handleInviteMore}
                handleDeleteGroupCart={handleDeleteGroupCart}
                handleLeaveGroupCart={handleLeaveGroupCart}
                handleRefreshGroupCart={handleRefreshGroupCart}
            />
        </Header>
    );
}

function GroupCartBodyWrapper(props: { children: ReactNode }): ReactElement | null {
    return <Content>{props.children}</Content>;
}

function GroupCartUIWrapper({ children, ...props }: GroupCartUIWrapperProps): React.ReactElement | null {
    const { state } = useContext(GroupOrderCartContext);
    const { data: groupCartData, error, getSharedCartLoading } = state;
    const [initialSharedCartLoaded, setInitialSharedCartLoaded] = useState(false);

    const handleBack = useCallback(() => {
        closeWebview();
    }, []);

    useCustomHardwareBack([CheckoutRoute.SHARED_CART], handleBack);

    useEffect(() => {
        // record page load time and app load time for ISC
        if (!getSharedCartLoading && groupCartData != null && !initialSharedCartLoaded) {
            recordSharedCartPerceivedTime();
            setInitialSharedCartLoaded(true);
        }
    }, [getSharedCartLoading, groupCartData, initialSharedCartLoaded, setInitialSharedCartLoaded]);

    const retryOnGenericError = useCallback(() => {
        // in case the user's normal flow of shared cart has failed, then it should reload
        window.location.reload();
    }, []);

    const onImpressionGenericError = useCallback(() => {
        recordNREvent({
            eventType: NEWRELIC_TABLES.GROUP_CART_GENERIC_ERROR_IMPRESSION,
            errorType: NR_PAGE_ERROR_TYPES.EMPTY_RESPONSE,
        });
    }, []);

    // loading state
    if (getSharedCartLoading) {
        return (
            <CartUIWrapper>
                <Shimmer />
            </CartUIWrapper>
        );
    }

    // empty response state
    if (isEmpty(groupCartData)) {
        if (isCurrentGroupOrderPlaced(error.statusCode) || isCurrentGroupOrderDeleted(error.statusCode)) {
            return null;
        }

        const errorActionPayload = {
            text: RETRY,
            onClick: retryOnGenericError,
        };
        return (
            <CartUIWrapper>
                <GenericError pageImpressionEvent={onImpressionGenericError} action={errorActionPayload} />
            </CartUIWrapper>
        );
    }

    return (
        <Container>
            <GroupCartHeaderWrapper {...props} groupCartData={groupCartData} />
            <GroupCartBodyWrapper>{children}</GroupCartBodyWrapper>
        </Container>
    );
}

export default memo(GroupCartUIWrapper);
